import axios from 'axios'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { uniqueId } from 'lodash'
import { useParams } from 'react-router-dom'

const ChatPreviewPanel: React.FC<any> = ({ data }) => {
  const [webhookUrl, setWebhookUrl] = useState<string>('')
  const [botIcon, setBotIcon] = useState<string>('')
  const [botName, setBotName] = useState<string>('')
  const [conversation, setConversation] = useState<any[]>([])
  const [inputText, setInputText] = useState<string>('')
  const [isTyping, setIsTyping] = useState(false)
  const [typingMessage, setTypingMessage] = useState('')
  const [showTypingDots, setShowTypingDots] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const { uuid } = useParams()

  const typeMessage = (html: string, delay = 15) =>
    new Promise<void>((resolve) => {
      let index = 0
      setTypingMessage('')
      const interval = setInterval(() => {
        setTypingMessage(html.slice(0, index + 1))
        index++
        if (index >= html.length) {
          clearInterval(interval)
          resolve()
        }
      }, delay)
    })

  // Improved HTML stripping using DOM
  const stripHtmlTags = (str: string) => {
    if (!str) return ''
    const tmp = document.createElement('DIV')
    tmp.innerHTML = str
    return tmp.textContent || tmp.innerText || ''
  }

  const initBotMessage = async () => {
    const appData = data.data.allApplicationId[0]
    const inputData = appData.input[0]
    if (inputData?.message) {
      setIsTyping(true)
      await typeMessage(inputData.message) // Pass HTML directly
      setConversation([{ side: 'lhs', message: inputData.message }])
      setIsTyping(false)
      setTypingMessage('')
    }
  }

  const handleRefresh = () => {
    setConversation([])
    initBotMessage()
  }

  const appData = useMemo(() => data?.data?.allApplicationId?.[0], [data?.data?.allApplicationId])

  // Fetch webhook URL from backend
  useEffect(() => {
    const fetchWebhookUrl = async () => {
      if (!uuid) {
        console.error('[ChatPreview] No bot ID (uuid) found')
        return
      }

      try {
        const baseUrl = process.env.REACT_APP_RESUME_PARSER_URL
        const token = localStorage.getItem('auth-token')
        const response = await axios.get(`${baseUrl}/ai-chatbots/webhook-url/${uuid}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        if (response.data?.webhook_url) {
          console.log('[ChatPreview] Webhook URL fetched:', response.data.webhook_url)
          setWebhookUrl(response.data.webhook_url)
        } else {
          console.error('[ChatPreview] No webhook URL in response:', response.data)
        }
      } catch (error: any) {
        console.error('[ChatPreview] Error fetching webhook URL:', error)
        // Fallback to template from config if available
        const webhookTemplate = appData?.config?.webhook?.endpoint || ''
        if (webhookTemplate) {
          console.log('[ChatPreview] Using webhook template from config:', webhookTemplate)
          setWebhookUrl(webhookTemplate)
        }
      }
    }

    if (uuid) {
      fetchWebhookUrl()
    }
  }, [uuid, appData])

  useEffect(() => {
    if (appData) {
      setConversation([])
      setBotName(appData.botName || 'Chatbot')
      setBotIcon(appData.config?.theme?.iconHref || '')
      initBotMessage()
    }
  }, [appData])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [conversation, typingMessage])

  const handleSend = async () => {
    if (inputText.trim() === '') return

    if (!webhookUrl) {
      console.error('[ChatPreview] No webhook URL configured')
      alert('Webhook URL is not configured. Please check bot settings.')
      return
    }

    const userMessage = inputText.trim()
    setConversation((prev) => {
      // Remove all tempDots, add user message and new tempDots
      const filtered = prev.filter((msg) => !msg.tempDots)
      return [
        ...filtered,
        { side: 'rhs', message: userMessage },
        { side: 'lhs', message: '...', tempDots: true }
      ]
    })
    setInputText('')

    try {
      const formData = new FormData()
      // Escape quotes in the question to prevent JSON parsing errors
      const escapedQuestion = userMessage.replace(/"/g, '\\"')
      formData.append('intent', `{"fulfillment":{"parameters":{"question":"${escapedQuestion}"}}}`)

      console.log('[ChatPreview] Sending to webhook:', webhookUrl)
      console.log('[ChatPreview] Question:', userMessage)

      const response: any = await axios.post(webhookUrl, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${localStorage.getItem('auth-token')}`,
          Accept: 'application/json'
        }
      })

      console.log('[ChatPreview] Response received:', response?.data)

      // Remove the dots message
      setConversation((prev) => prev.filter((msg) => !msg.tempDots))
      setShowTypingDots(false)

      let reply = response?.data?.message || 'No response received'
      setIsTyping(true)
      await typeMessage(reply) // For animation, use HTML directly
      setConversation((prev) => [...prev, { side: 'lhs', message: reply }]) // Store HTML for rendering
      setIsTyping(false)
      setTypingMessage('')
    } catch (error: any) {
      // Remove the dots message even on error
      setConversation((prev) => prev.filter((msg) => !msg.tempDots))
      setShowTypingDots(false)
      console.error('[ChatPreview] Error calling API:', error)
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to get response. Please try again.'
      setIsTyping(true)
      await typeMessage(`<p style="color: #ef4444;">Error: ${errorMessage}</p>`)
      setConversation((prev) => [
        ...prev,
        { side: 'lhs', message: `<p style="color: #ef4444;">Error: ${errorMessage}</p>` }
      ])
      setIsTyping(false)
      setTypingMessage('')
    }
  }

  return (
    <PreviewPanel>
      <ChatHeader>
        <LeftHeader>
          <BotAvatar>
            <img src={botIcon} alt='Bot' />
          </BotAvatar>
          <BotName>{botName}</BotName>
        </LeftHeader>
        <RefreshIcon onClick={handleRefresh} title='Refresh Chat'>
          <svg
            fill='#000000'
            height='20px'
            width='20px'
            viewBox='0 0 383.748 383.748'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g>
              <path d='M62.772,95.042C90.904,54.899,137.496,30,187.343,30c83.743,0,151.874,68.13,151.874,151.874h30 C369.217,81.588,287.629,0,187.343,0c-35.038,0-69.061,9.989-98.391,28.888C70.368,40.862,54.245,56.032,41.221,73.593 L2.081,34.641v113.365h113.91L62.772,95.042z' />
              <path d='M381.667,235.742h-113.91l53.219,52.965c-28.132,40.142-74.724,65.042-124.571,65.042 c-83.744,0-151.874-68.13-151.874-151.874h-30c0,100.286,81.588,181.874,181.874,181.874c35.038,0,69.062-9.989,98.391-28.888 c18.584-11.975,34.707-27.145,47.731-44.706l39.139,38.952V235.742z' />
            </g>
          </svg>
        </RefreshIcon>
      </ChatHeader>

      <ChatPreview>
        <MessagesWrapper>
          <MessagesContainer>
            {conversation.map((msg, index) => (
              <MessageGroup key={uniqueId()} side={msg.side}>
                {msg.side === 'lhs' && (
                  <BotAvatar small>
                    <img src={botIcon} alt='Bot' />
                  </BotAvatar>
                )}
                <ChatMessage
                  side={msg.side}
                  {...(msg.side === 'lhs'
                    ? { dangerouslySetInnerHTML: { __html: msg.message } }
                    : {})}
                >
                  {msg.side === 'rhs' ? msg.message : null}
                </ChatMessage>
              </MessageGroup>
            ))}

            {isTyping && typingMessage && (
              <MessageGroup side='lhs'>
                <BotAvatar small>
                  <img src={botIcon} alt='Bot' />
                </BotAvatar>
                <ChatMessage
                  side='lhs'
                  {...(true ? { dangerouslySetInnerHTML: { __html: typingMessage } } : {})}
                >
                  {null}
                </ChatMessage>
              </MessageGroup>
            )}

            <div ref={messagesEndRef} />
          </MessagesContainer>
        </MessagesWrapper>

        <InputContainer>
          <ChatInput
            placeholder='Type here'
            value={inputText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputText(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              e.key === 'Enter' && handleSend()
            }
          />
          <SendButton onClick={handleSend}>
            <SendIcon>
              <svg
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M5 12H19M19 12L12 5M19 12L12 19'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </SendIcon>
          </SendButton>
        </InputContainer>
      </ChatPreview>
    </PreviewPanel>
  )
}

export default ChatPreviewPanel
const PreviewPanel = styled.div`
  height: 90vh;
  display: flex;
  flex-direction: column;
  background-color: #f8fafc;
  border-left: 1px solid #e9ecef;
  margin-left: 16px;
`

const ChatHeader = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  border-bottom: 1px solid #e9ecef;
`

const LeftHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const BotAvatar = styled.div<{ small?: boolean }>`
  width: ${(props) => (props.small ? '32px' : '40px')};
  height: ${(props) => (props.small ? '32px' : '40px')};
  border-radius: 50%;
  overflow: hidden;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const BotName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #1a1a1a;
`

const RefreshIcon = styled.div`
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.2s;
  color: #4b5563;

  &:hover {
    background-color: #f1f5f9;
  }

  svg {
    display: block;
  }
`

const ChatPreview = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const MessagesWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
`

const MessagesContainer = styled.div`
  padding: 16px;
`

const MessageGroup = styled.div<{ side?: string }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  justify-content: ${(props) => (props.side === 'rhs' ? '' : 'flex-start')};
  flex-direction: ${(props) => (props.side === 'rhs' ? 'row-reverse' : 'row')};
`

const ChatMessage = styled.div<{ side?: string }>`
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  max-width: 80%;
  background-color: ${(props) => (props.side === 'rhs' ? '#daf1ff' : 'white')};
  color: ${(props) => (props.side === 'rhs' ? '#0f172a' : '#1a1a1a')};
`

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #e9ecef;
  background: white;
`

const ChatInput = styled.input`
  width: 100%;
  padding: 12px;
  padding-right: 44px;
  border: 1px solid #e9ecef;
  border-radius: 24px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #7c3aed;
  }

  &::placeholder {
    color: #94a3b8;
  }
`

const SendButton = styled.button`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: #10b981;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #059669;
  }
`

const SendIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(45deg);
`
